import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { UserCog, Timer } from "lucide-react";
import { usePersona, authorityShort } from "@/context/PersonaContext";

const WINDOWS = [
  { label: "15 minutes", minutes: 15 },
  { label: "30 minutes", minutes: 30 },
  { label: "1 hour", minutes: 60 },
];

/**
 * DelegateModal · lets the current holder of a delegable authority
 * grant it to another named persona for a bounded window.
 * The grant, the expiration, and any manual revoke are audit-logged
 * by PersonaContext.
 */
export default function DelegateModal({ open, onClose, authority = "production-commitment" }) {
  const { active, personas, setDelegation } = usePersona();
  const [toId, setToId] = useState("");
  const [minutes, setMinutes] = useState(15);
  const [reason, setReason] = useState("Out of office · site walk at Warsaw");

  // Choose the first non-active persona as the default delegate on open.
  useEffect(() => {
    if (open && active) {
      const first = Object.values(personas).find((p) => p.id !== active.id);
      setToId(first ? first.id : "");
    }
  }, [open, active, personas]);

  const eligible = active
    ? Object.values(personas).filter((p) => p.id !== active.id)
    : [];

  const onConfirm = () => {
    if (!toId) return;
    setDelegation({ to: toId, authority, durationMinutes: minutes, reason });
    toast.success(
      `Authority delegated · ${authorityShort[authority]} → ${personas[toId].name} · ${minutes}m window`,
      { duration: 4200 }
    );
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? null : onClose())}>
      <DialogContent
        className="max-w-[560px] p-0 gap-0 overflow-hidden"
        style={{ background: "var(--mos-surface)" }}
        data-testid="delegate-modal"
      >
        <div
          className="px-6 pt-6 pb-5 border-b"
          style={{ borderColor: "var(--mos-border)" }}
        >
          <DialogHeader>
            <div className="flex items-center gap-3">
              <span
                className="h-9 w-9 rounded-full inline-flex items-center justify-center"
                style={{ background: "#EFF3F9" }}
              >
                <UserCog size={17} style={{ color: "var(--mos-accent)" }} />
              </span>
              <DialogTitle className="text-[18px] font-semibold text-slate-900">
                Delegate authority
              </DialogTitle>
            </div>
          </DialogHeader>
          <p className="mt-4 text-[13.5px] text-slate-600 leading-relaxed">
            Delegating grants your{" "}
            <span className="mos-mono text-slate-900">
              {authorityShort[authority]}
            </span>{" "}
            authority to another named user for a bounded window. This grant is
            itself audit logged. It ends when the window expires or when you
            revoke it.
          </p>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Delegate */}
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
              Delegate to
            </div>
            <div className="space-y-2">
              {eligible.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setToId(p.id)}
                  data-testid={`delegate-to-${p.id}`}
                  className={
                    "w-full flex items-center gap-3 rounded-md border px-3 py-2.5 text-left transition-colors " +
                    (toId === p.id
                      ? "border-[color:var(--mos-accent)] bg-[color:#EFF3F9]"
                      : "border-[color:var(--mos-border)] hover:border-[color:var(--mos-border-strong)]")
                  }
                >
                  <span
                    className="h-8 w-8 rounded-full flex items-center justify-center text-[11px] font-semibold mos-mono flex-none"
                    style={{ background: "#5B7191", color: "#FFFFFF" }}
                  >
                    {p.initials}
                  </span>
                  <span className="flex-1 min-w-0">
                    <div className="text-[13.5px] font-medium text-slate-900">
                      {p.name}
                    </div>
                    <div className="text-[11.5px] text-slate-500">
                      {p.role} · {p.email}
                    </div>
                  </span>
                  <input
                    type="radio"
                    name="delegate-to"
                    checked={toId === p.id}
                    onChange={() => setToId(p.id)}
                    className="flex-none"
                    aria-label={`delegate to ${p.name}`}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Window */}
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-2">
              <Timer size={11} />
              Window
            </div>
            <div className="flex items-center gap-2">
              {WINDOWS.map((w) => (
                <button
                  key={w.minutes}
                  type="button"
                  onClick={() => setMinutes(w.minutes)}
                  data-testid={`delegate-window-${w.minutes}`}
                  className={
                    "flex-1 rounded-md border py-2 text-[13px] font-medium transition-colors " +
                    (minutes === w.minutes
                      ? "border-[color:var(--mos-accent)] bg-[color:#EFF3F9] text-[color:var(--mos-accent)]"
                      : "border-[color:var(--mos-border-strong)] text-slate-700 hover:border-[color:var(--mos-accent)]")
                  }
                >
                  {w.label}
                </button>
              ))}
            </div>
          </div>

          {/* Reason */}
          <div>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                Reason (optional · logged)
              </span>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="delegate-reason"
                className="mt-1.5 w-full rounded-md border px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-[color:var(--mos-accent-ring)] focus:border-[color:var(--mos-accent)]"
                style={{
                  borderColor: "var(--mos-border-strong)",
                  color: "var(--mos-text)",
                }}
              />
            </label>
          </div>
        </div>

        <div
          className="px-6 py-4 border-t flex items-center justify-end gap-3"
          style={{ background: "#FAFBFC", borderColor: "var(--mos-border)" }}
        >
          <button
            type="button"
            onClick={onClose}
            className="mos-btn mos-btn-secondary"
            data-testid="btn-cancel-delegate"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!toId}
            className={
              "mos-btn " +
              (toId
                ? "mos-btn-primary"
                : "mos-btn-secondary cursor-not-allowed opacity-60")
            }
            data-testid="btn-confirm-delegate"
          >
            Delegate and log
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
